import { EmbedHeader, PageTitleHelmet, PeachjarLoader } from '@/components';
import { DEFAULT_ERROR_MESSAGE, FlyerType, VariantEnum } from '@/config/constants';
import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import AuthenticationContext from '@/context/AuthenticationContext';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { useSnackbar } from '@/design-system';
import { READ_FLYER_MUTATION } from '@/graphql/mutation';
import { flyerVar, openLoginDrawerVar, userInformationVar } from '@/graphql/state';
import { ReadFlyerResponse } from '@/graphql/types';
import { TrackingFrom, useFlyer, useIsEmbed, useMetrics, useTrackingLocation } from '@/hooks';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useNavigationPreviousPage } from '@/hooks/useNavigationPreviousPage';
import { FlyerStatus } from '@/types/flyer';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import scrollToTop from '@/utils/scroolToTop';
import { ApolloCache, useApolloClient, useMutation, useReactiveVar } from '@apollo/client';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ActiveFlyerDetails,
  ExpiredFlyerDetails,
  FlyerDetailsRemoved,
  FlyerDetailsStatusModal,
  PendingFlyerDetails,
} from './components';
import { GET_FLYER_DISTRIBUTION_INFO } from '@/graphql/queries';
import { OwningTypeEnum } from '@/graphql/types';

export const FlyerDetails: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const { trackMetrics } = useMetrics();
  const client = useApolloClient();
  const trackingLocation = useTrackingLocation();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const audienceId = searchParams.get('audienceId');
  const audienceType = searchParams.get('audienceType');
  const originParam = searchParams.get('origin');
  const isEmbedded = useIsEmbed();
  const { language } = useContext(UserPreferencesContext);

  const navigatePreviousPage = useNavigationPreviousPage();
  const [loadingFlyer, setLoadingFlyer] = useState(true);

  const showToast = useSnackbar();
  const { loading } = useFlyer({
    flyerId: params?.id,
    districtId: params?.districtId,
    schoolId: audienceId,
  });
  const flyer = useReactiveVar(flyerVar);
  const flyerIsLoaded = useMemo(() => !!flyer && !loadingFlyer, [flyer, loadingFlyer]);

  const user = useReactiveVar(userInformationVar);
  const { isAuthenticated } = useContext(AuthenticationContext);
  const openLoginDrawer = useReactiveVar(openLoginDrawerVar);
  const flyerType = flyer?.type ?? FlyerType.school;
  const isCommunity = flyerType === FlyerType.community;
  const isCommunityFree = !!flyer?.isCommunityFree;

  const trackId = 'flyer_details_page';
  const flyerStatus = flyer ? flyer?.status : FlyerStatus.removed;

  const isNonActive = flyerStatus !== FlyerStatus.active;

  const trackingFrom: TrackingFrom = useMemo(
    () => ({
      event_from: TrackingEvents.viewFlyerDetails,
      event_detail_from: EventDetails.viewFlyerDetails(flyerType),
      stage_from: TrackingStage.consideration,
    }),
    [flyerType]
  );

  const { track } = useAnalytics();
  const [readFlyer] = useMutation<ReadFlyerResponse>(READ_FLYER_MUTATION);

  const goToExploreFlyers = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:go_to_explore_page:all_flyers`,
      label: 'Go to Explore All Flyers Page',
    });
    navigate(`/explore/all?tab=school`);
  };

  const flyerStates = {
    [FlyerStatus.expired]: {
      title: 'This flyer has expired.',
      variant: VariantEnum.error,
    },
    [FlyerStatus.removed]: {
      title: 'This flyer can not be found.',
      variant: VariantEnum.info,
    },
    [FlyerStatus.pending]: {
      title: 'This flyer is pending approval.',
      variant: VariantEnum.info,
    },
  };

  const updateFlyerAsRead = async () => {
    if (flyer) {
      if (!flyer.isRead) {
        try {
          const { data } = await readFlyer({
            variables: {
              input: {
                flyerId: flyer.flyerId,
                isRead: true,
              },
            },
            fetchPolicy: 'network-only',
            update: (cache, { data: mutationData }) => {
              if (mutationData?.readFlyer) {
                updateFlyerReadStatus(client.cache, flyer.flyerId);
              }
            },
            refetchQueries: [
              {
                query: GET_FLYER_DISTRIBUTION_INFO,
                variables: {
                  input: {
                    owningType:
                      flyerType === FlyerType.school
                        ? OwningTypeEnum.district
                        : OwningTypeEnum.organization,
                  },
                },
              },
            ],
          });
        } catch (err) {
          showToast({ message: DEFAULT_ERROR_MESSAGE });
        }
      }
    }
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (originParam === 'email') {
      trackEvent({
        event: TrackingEvents.receiveNotification,
        event_detail: EventDetails.emailNotification,
        stage: TrackingStage.connection,
      });
    }
  }, [originParam]);

  useEffect(() => {
    if (!flyer) return;
    if (flyerIsLoaded) {
      if (isAuthenticated) {
        updateFlyerAsRead();
      }

      const action = 'pa-viewflyer';

      trackMetrics({
        flyerId: flyer.flyerId.toString(),
        resource: 'districts',
        resourceId: flyer?.districtId.toString() ?? '',
        audienceId: audienceId,
        action,
        metric: action,
        distributionsReleased: flyer.distributionsReleased,
      });

      const eventDetails = EventDetails.viewFlyerDetails(flyerType);

      trackEvent({
        event: TrackingEvents.viewFlyerDetails,
        event_detail: eventDetails,
        stage: TrackingStage.consideration,
        ...(originParam === 'email'
          ? {
              event_from: TrackingEvents.receiveNotification,
              event_detail_from: EventDetails.emailNotification,
              stage_from: TrackingStage.connection,
            }
          : {}),
      });
    }
  }, [flyerIsLoaded]);

  const showLoginDrawer = () => {
    if (!isAuthenticated && !openLoginDrawer) {
      openLoginDrawerVar(true);
      return true;
    }
    return false;
  };

  const goToPreviousPage = () => {
    if (trackingLocation || isAuthenticated) {
      navigatePreviousPage();
      track({
        action: GenericTrackTypes.BUTTON_PRESSED,
        category: `${trackId}:back_button`,
        label: flyer?.title,
        value: Number(flyer?.flyerId),
      });
    } else {
      showLoginDrawer();
    }
  };

  const onLoadFlyer = () => setLoadingFlyer(false);

  if (loading) return <PeachjarLoader loading={loading} />;

  const translatedTitle = flyer?.titleTranslations
    ? flyer.titleTranslations[language.code]
    : undefined;
  const flyerTitle = translatedTitle ?? flyer?.title;

  const props = {
    flyer: flyer!,
    loadingFlyer,
    onLoadFlyer,
    backButton: goToPreviousPage,
    trackingFrom,
    flyerTitle: flyerTitle!,
  };

  const flyeComponents = {
    [FlyerStatus.active]: (
      <ActiveFlyerDetails
        {...props}
        trackId={trackId}
        showLoginDrawer={showLoginDrawer}
        isCommunity={isCommunity}
        isCommunityFree={isCommunityFree}
        isEmbedded={isEmbedded}
      />
    ),
    [FlyerStatus.removed]: (
      <FlyerDetailsRemoved
        goToPreviousPage={goToPreviousPage}
        goToExploreFlyers={goToExploreFlyers}
      />
    ),
    [FlyerStatus.pending]: <PendingFlyerDetails {...props} />,
    [FlyerStatus.expired]: <ExpiredFlyerDetails {...props} isCommunity={isCommunity} />,
  };

  const currentFlyerComponent = flyeComponents[flyerStatus];

  return (
    <>
      <PageTitleHelmet>Flyer</PageTitleHelmet>
      {isEmbedded && (
        <EmbedHeader
          districtId={params?.districtId}
          audienceId={audienceId}
          audienceType={audienceType}
        />
      )}
      {currentFlyerComponent}
      {isNonActive && <FlyerDetailsStatusModal trackId={trackId} {...flyerStates[flyerStatus]} />}
    </>
  );
};

export function updateFlyerReadStatus(cache: ApolloCache<object>, flyerId: number) {
  cache.modify({
    id: `Flyer:{"flyerId":${flyerId}}`,
    fields: {
      isRead: () => true,
    },
  });

  cache.modify({
    fields: {
      getAllFlyers: (existingData, { readField }) => {
        if (!existingData || !existingData.items) return existingData;
        const hasFlyer = existingData.items.some((item: any) => {
          const itemId = readField('flyerId', item);
          return itemId === flyerId;
        });

        if (hasFlyer) {
          const updatedItems = existingData.items.map((item: any) => {
            const itemId = readField('flyerId', item);
            if (itemId === flyerId) {
              return {
                ...item,
                isRead: true,
              };
            }
            return item;
          });

          return {
            ...existingData,
            items: updatedItems,
          };
        }

        return existingData;
      },
    },
  });
}
